import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import RarityBadge from '@/components/RarityBadge';
import OrderDetailsModal from '@/components/OrderDetailsModal';
import { ORDER_STATUS, getOrderStatusTranslationKey } from '@/config/orderStatus';
import { formatDate, formatCurrency } from '@/utils/formatUtils';
import OfficialTag from './OfficialTag';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

const OrderCard = ({ order, onConfirmReceipt, onStatusClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const getGrade = () => {
    if (order.listing.psa) {
      return order.listing.psa.replace('PSA_', 'PSA ');
    }
    if (order.listing.bundledQuantity) {
      const unit = order.listing.product?.boxType === 'PACK' ? t('box.pack') : t('box.box_unit');
      return `${order.listing.bundledQuantity} ${unit}`;
    }
    if (order.listing.condition) {
      return t(`sell.product_info.conditions.${order.listing.condition.toLowerCase()}`);
    }
    return '-';
  };

  // Helper to get a displayable status text
  const getStatusText = (status, isOfficial) => {
    return t(getOrderStatusTranslationKey(status, isOfficial));
  };

  const handleOpenDetailsModal = () => {
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
  };

  // Handler for confirm receipt button
  const handleConfirmReceipt = () => {
    onConfirmReceipt(order);
  };

  const handleCardNameClick = () => {
    if (order.listing.type === 'NON_PSA_SINGLE_CARD') {
      navigate(`/listing/${order.listing.id}`);
    } else {
      navigate(`/card/${order.listing.product.id}`);
    }
  };

  const handleStatusClick = () => {
    if (onStatusClick) {
      onStatusClick(order.id);
    }
  };

  return (
    <>
      <div className="grid grid-cols-[1fr,108px,108px,108px,180px] gap-6 py-6">
        <div className="w-full flex justify-start items-center gap-4">
          <div className="w-[100px] h-[100px] relative bg-white rounded overflow-hidden">
            <img 
              src={getProductThumbnailUrl(order.listing.product.images?.[0]?.url)}
              alt={order.listing.product.localizedName}
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex-1 inline-flex flex-col justify-start items-start">
            <div>
              <div className="min-w-0">
                {order.listing.isOfficial && (
                  <OfficialTag className="align-middle mr-1.5" />
                )}
                <span 
                  className="align-middle inline min-w-0 text-[#111111] text-sm font-normal hover:underline cursor-pointer whitespace-normal break-words leading-[18px]"
                  onClick={handleCardNameClick}
                >
                  {order.listing.product.localizedRawName || order.listing.product.localizedName}
                  {order.listing.product.rarityInfo?.sub?.name ? `(${order.listing.product.rarityInfo.sub.name})` : ''}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {order.listing.product.rarityInfo?.main && (
                  <RarityBadge rarity={order.listing.product.rarityInfo.main} />
                )}
                {(order.listing.product?.set?.code || (order.listing.product?.rawCode && order.listing.product?.type !== 'CARD_BOX')) && (
                  <div className="h-[18px] px-1 bg-[#f4f4f4] rounded-[3px] outline outline-[0.50px] outline-offset-[-0.50px] outline-[#dddddd] flex justify-start items-center gap-2.5">
                    <div className="justify-start text-[#111111] text-sm font-normal leading-[14px]">
                      {order.listing.product?.type === 'CARD_BOX' ? order.listing.product?.set?.code : (order.listing.product?.set?.code && order.listing.product?.rawCode ? `${order.listing.product.set.code} ${order.listing.product.rawCode}` : order.listing.product?.set?.code || order.listing.product?.rawCode)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex flex-col justify-center items-start gap-1">
              <div className="text-[#aaaaaa] text-xs font-normal">
                {order.status === ORDER_STATUS.COMPLETED ? (
                  <>
                    {t('common.finishDate')} {formatDate(order.finishedAt)}
                  </>
                ) : (
                  <>
                    {t('common.orderDate')} {formatDate(order.createdAt)}
                  </>
                )}
              </div>
              <div className="text-[#aaaaaa] text-xs font-normal">
                {t('common.orderNumber')} {order.number}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-center items-center text-[#666666] text-sm font-normal">
          {getGrade()}
        </div>
        <div className="flex justify-center items-center text-center text-[#666666] text-sm font-normal">
          {formatCurrency(order.monetary.price)}
        </div>
        <div
          className="flex justify-center items-center text-center text-[#666666] text-sm font-normal cursor-pointer hover:text-[#3b5493] hover:underline"
          onClick={handleStatusClick}
        >
          {getStatusText(order.status, order.listing.isOfficial)}
        </div>
        <div className="inline-flex flex-col justify-center items-center gap-1.5">
          {order.status === ORDER_STATUS.WAREHOUSE_SHIPPED && (
            <Button
              variant="primary"
              size="s"
              className="w-24 mb-1"
              onClick={handleConfirmReceipt}
            >
              {t('orders.confirmReceipt.title')}
            </Button>
          )}
          <Button
            variant="default"
            size="s"
            className="w-24"
            onClick={handleOpenDetailsModal}
          >
            {t('common.details')}
          </Button>
        </div>
      </div>

      {/* Order Details Modal */}
      <OrderDetailsModal 
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        order={order}
        onConfirmReceipt={handleConfirmReceipt}
      />
    </>
  );
};

export default OrderCard;
